import type { DiscountDetail } from '@ahm/api-wrappers-core';
import {
  Button,
  Center,
  CloseButton,
  List,
  rem,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  closeAllModals,
  closeModal,
  type ContextModalProps,
} from '@mantine/modals';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { ModalName } from '../..';
import { usePromotionEstimate } from '../hooks/use-promotion-estimate';
import WarningIcon from '@/assets/icons/warning-triangle-icon.svg';
import { EXCEEDED_DISCOUNT_LIMIT } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type ExceedDiscountLimitModalProps = {
  onClose?: () => void;
  discountDetails: DiscountDetail[];
};

function ExceedDiscountLimitModal({
  innerProps: { discountDetails, onClose },
}: ContextModalProps<ExceedDiscountLimitModalProps>) {
  const isMobile = useIsMobile();
  const tCommon = useTranslations('common');
  const tPromo = useTranslations('booking.promo.exceed_discount_limit');
  const { applyValidPromoCodesFromExceedModal } = usePromotionEstimate();

  const exceededList = useMemo(
    () =>
      discountDetails?.filter(
        (item: any) => item.error_code === EXCEEDED_DISCOUNT_LIMIT
      ),
    [discountDetails]
  );

  const handleAgree = () => {
    applyValidPromoCodesFromExceedModal(discountDetails);
    closeAllModals();
  };

  const handleClose = () => {
    onClose?.();
    closeModal(ModalName.ExceedDiscountLimit);
  };

  return (
    <Stack>
      <CloseButton
        pos="absolute"
        iconSize={rem(24)}
        variant="transparent"
        onClick={handleClose}
        c="neutral.6"
        top={rem(16)}
        style={
          isMobile
            ? {
                left: rem(16),
                outline: 'none',
              }
            : {
                right: rem(16),
                outline: 'none',
              }
        }
      />

      <Stack gap={rem(24)} mt={rem(24)}>
        <Center>
          <WarningIcon size={rem(64)} />
        </Center>

        <Stack gap={rem(8)}>
          <Title order={3} ta="center" c="neutral.7">
            {tPromo('title')}
          </Title>

          <Stack>
            <Title order={isMobile ? 5 : 4} c="neutral.6" fw={500}>
              {tPromo('description')}
            </Title>

            <List
              px="xs"
              styles={{
                itemLabel: {
                  fontSize: isMobile ? rem(14) : rem(16),
                  lineHeight: rem(20),
                  fontWeight: 700,
                },
              }}
            >
              {exceededList?.map((item) => (
                <List.Item key={item.promo_code_id}>
                  {(item as any).description || (item as any).promo_code_id}
                </List.Item>
              ))}
            </List>

            <Text size={isMobile ? 'sm' : 'md'} fw={500} c="neutral.6">
              {tPromo('question')}
            </Text>
          </Stack>
        </Stack>
      </Stack>

      <SimpleGrid cols={1} spacing={rem(8)}>
        <Button onClick={handleAgree} h={rem(48)}>
          {tCommon('agree')}
        </Button>
        <Button variant="subtle" h={rem(48)} onClick={handleClose}>
          {tCommon('actions.close')}
        </Button>
      </SimpleGrid>
    </Stack>
  );
}

export default ExceedDiscountLimitModal;
